/**
 * ویرایشِ سیگنال — قرینه‌ی apps/mobile/app/signal/edit/[id].tsx.
 *
 * همون تقسیمِ موبایل: این‌جا **محتوا**، **فهرستِ دعوت‌شده‌ها**، و **دعوتِ یک
 * آدمِ تازه از بیرونِ اپ** (WHOSUP-203). بقیه‌ی راه‌های رساندن — «همه‌ی
 * دوستانم» و لینکِ عمومی — کارهای یک‌باره‌ی لحظه‌ی فرستادنن و سیگنال قبلاً
 * رفته.
 *
 * ---------- دوستِ دفترچه‌ای (WHOSUP-231) ----------
 * انتخابگرِ آدم‌ها — مثل فرمِ ساخت — دو منبع دارد: دوستانِ اپ، و دوستانی که
 * هنوز حساب ندارند (`contactFriendRepo`). دومی‌ها از همان مسیرِ دعوتِ ایمیلیِ
 * پایین می‌روند، نه از مسیرِ کاربرِ حساب‌دار. دوستِ فقط-شماره‌تلفن همان‌جا
 * دیده می‌شود ولی انتخاب‌شدنی نیست (WHOSUP-143) — مرورگر پیامک ندارد.
 *
 * ---------- شماره روی وب ----------
 * مرورگر پیامک نمی‌فرسته (همون استثنای parity که فرمِ ساخت هم داره). ولی
 * ردیفِ دعوت و لینکِ شخصی رو همین‌جا می‌سازیم: بعد از ذخیره، صفحه‌ی برنامه
 * برای همون نفر دکمه‌ی «کپیِ لینکِ شخصی» نشون می‌ده و کاربر از هر راهی که
 * می‌خواد می‌فرستدش. ایمیل برخلافش خودش می‌ره، چون سرور می‌فرستدش.
 *
 * و مثل موبایل، «کسی که گفته هستم» دکمه‌ی برداشتن نداره. قانونِ واقعی توی
 * سیاستِ `r_del` دیتابیسه؛ این فقط نشونش می‌ده.
 */
import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { signalRepo } from '@/core/data/signalRepo';
import { inviteRepo } from '@/core/data/inviteRepo';
import { phoneRepo } from '@/core/data/phoneRepo';
import { friendRepo } from '@/core/data/socialRepo';
import { contactFriendRepo } from '@/core/data/contactFriendRepo';
import { tagRepo } from '@/core/data/miscRepo';
import { useSession } from '@/core/session';
import { kindsFor } from '@/core/domain/signalKinds';
import { APPROX_PRESETS, parseApprox } from '@/core/lib/approxTime';
import { signalWhenLabel } from '@/core/lib/dates';
import { t, useLocale } from '@/core/lib/i18n';
import { reportError } from '@/core/lib/report';
import { handleFilterBlocked } from '@/ui/filterBlocked';
import { trackFunnel } from '@/core/lib/analytics';
import {
  addDraftContact, contactInvitees, draftContactLabel, emailInvitesOf, invitedSoFar,
  phoneInvitesOf, removeDraftContact, type ContactInvitee, type DraftContact,
} from '@/core/lib/inviteDraft';
import { SIGNAL_NOTE_KEYS } from '@/core/lib/signalNote';
import {
  UNACCEPTED_INVITE_CAP, canInvite, inviteCapHint, isInviteCapError,
} from '@/core/lib/inviteCap';
import { Card, Help, Note, PickList, Select } from '@/ui/kit';
import { TagPicker } from '@/ui/TagPicker';
import { MultiSelect } from '@/ui/MultiSelect';
import { tagIdsOf, type SignalEditDraft, type SignalKind, type TimeMode } from '@/core/domain/types';

type WhenMode = 'approx' | 'coordinate' | 'exact' | 'free';

export function EditSignalPage() {
  useLocale();
  const { id } = useParams<{ id: string }>();
  const { profile } = useSession();
  const qc = useQueryClient();
  const nav = useNavigate();

  const signal = useQuery({
    queryKey: ['signal', id],
    queryFn: () => signalRepo.byId(id!, profile!.id),
    enabled: !!id && !!profile,
  });
  const recipients = useQuery({
    queryKey: ['recipients', id],
    queryFn: () => signalRepo.recipients(id!),
    enabled: !!id,
  });
  const tags = useQuery({ queryKey: ['tags'], queryFn: tagRepo.list });
  const friends = useQuery({
    queryKey: ['friends'],
    queryFn: () => friendRepo.list(profile!.id),
    enabled: !!profile,
  });
  /** دوستانِ بی‌حساب — همون کلیدِ کشِ فرمِ ساخت و صفحه‌ی دوستان. */
  const contactFriends = useQuery({
    queryKey: ['contactFriends'],
    queryFn: contactFriendRepo.list,
    enabled: !!profile,
  });

  const [filledFrom, setFilledFrom] = useState<string | null>(null);
  const [kind, setKind] = useState<SignalKind>('definite');
  const [title, setTitle] = useState('');
  /** شرحِ آزاد — بدونِ این فیلد، هر ذخیره‌ی ویرایش note قبلی رو پاک می‌کرد. */
  const [note, setNote] = useState('');
  const [tagIds, setTagIds] = useState<number[]>([]);
  const [whenMode, setWhenMode] = useState<WhenMode>('approx');
  const [preset, setPreset] = useState<string>(APPROX_PRESETS[0].token);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [freeWhen, setFreeWhen] = useState('');
  const [place, setPlace] = useState('');
  /**
   * فیلدِ هزینه از فرم برداشته شد (حالا یکی از پیشنهادهای شرحه)، ولی مقدارِ
   * سیگنال‌های قدیمی همین‌جا خونده و دست‌نخورده پس فرستاده می‌شه — وگرنه
   * اولین ذخیره‌ی ویرایش بی‌صدا پاکش می‌کرد.
   */
  const [cost, setCost] = useState('');
  const [chain, setChain] = useState(0);
  /** ظرفیت/مهلت/نصاب (مایگریشن 0131) — متن، تا فیلدِ عددی خالی‌شدنی بماند. */
  const [capacity, setCapacity] = useState('');
  const [quorum, setQuorum] = useState('');
  const [deadlineDate, setDeadlineDate] = useState('');
  const [deadlineTime, setDeadlineTime] = useState('');
  /** «گزینه‌های بیشتر» (زنجیره + مهلت) — پیش‌فرض بسته (WHOSUP-282). */
  const [moreOpen, setMoreOpen] = useState(false);
  const moreShown = moreOpen || chain !== 0 || !!deadlineDate;
  const [adding, setAdding] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  /** آدم‌های بیرون از اپ که همین حالا تایپ شدن (WHOSUP-203). */
  const [outsideOpen, setOutsideOpen] = useState(false);
  const [outside, setOutside] = useState<DraftContact[]>([]);
  const [reachDraft, setReachDraft] = useState('');
  const [reachName, setReachName] = useState('');
  const [reachError, setReachError] = useState<string | null>(null);

  /**
   * کدِ کشورِ خودِ کاربر، برای شماره‌ای که بدونِ «+» تایپ می‌شه (WHOSUP-162).
   * خودِ repo کش داره، پس این کوئری فقط برای اینه که رندر منتظرِ promise نمونه.
   */
  const callingCode = useQuery({
    queryKey: ['myCallingCode'],
    queryFn: () => phoneRepo.myCallingCode(),
  });

  /**
   * یک آدم به فهرستِ «از بیرون» اضافه می‌کنه.
   *
   * «تکراری» و «پر» خطا نیستن، فقط کاری برای انجام دادن نیست — پیامِ قرمز
   * برای «قبلاً اضافه‌ش کردی» بیشتر گیج می‌کنه تا کمک. همون رفتارِ فرمِ ساخت.
   */
  const addOutside = () => {
    // سقف و تکراری‌های سرور روی ردیف‌های موجود حساب می‌شن، پس هر دو از
    // همون فهرستِ گیرنده‌هایی درمیان که همین صفحه از قبل داره.
    const already = invitedSoFar(recipients.data ?? []);
    const r = addDraftContact(outside, reachDraft, reachName, {
      callingCode: callingCode.data, room: already.room, taken: already.taken,
    });
    if (!r.ok) {
      setReachError(r.reason === 'invalid' ? t('inviteReachBad') : null);
      if (r.reason !== 'invalid') { setReachDraft(''); setReachName(''); }
      return;
    }
    setOutside(r.list);
    setReachDraft('');
    setReachName('');
    setReachError(null);
  };

  /**
   * انتخاب/برداشتنِ یه دوستِ بی‌حساب (WHOSUP-231).
   *
   * می‌شینه توی همون فهرستِ «از بیرون»، فقط با `contactId` — یعنی موقعِ ذخیره
   * از همون مسیرِ ایمیل می‌ره، و چیپش پایینِ کارت تکرار نمی‌شه چون انتخابگر
   * خودش نشونش داده.
   */
  const toggleBookContact = (row: ContactInvitee<{ id: number; name: string }>) => {
    if (!row.value || !row.channel) return;
    if (outside.some((c) => c.contactId === row.contact.id)) {
      setOutside(removeDraftContact(outside, row.channel, row.value));
      return;
    }
    const already = invitedSoFar(recipients.data ?? []);
    const r = addDraftContact(outside, row.value, row.contact.name, {
      callingCode: callingCode.data,
      room: already.room,
      taken: already.taken,
      contactId: row.contact.id,
    });
    if (r.ok) setOutside(r.list);
  };

  const s = signal.data;
  /**
   * فرم فقط یک بار پر می‌شه.
   *
   * یه `useEffect` که به `signal.data` گوش بده، با هر تازه‌سازیِ کوئری
   * نوشته‌ی نیمه‌کاره‌ی کاربر رو پاک می‌کنه. پس شرطِ «از کدوم سیگنال پر
   * شدم» نگه داشته می‌شه — رندرِ بعدی دیگه دست نمی‌زنه.
   */
  if (s && filledFrom !== s.id) {
    setFilledFrom(s.id);
    setTitle(s.title);
    setKind(s.kind);
    setNote(s.note ?? '');
    setTagIds(tagIdsOf(s));
    setPlace(s.placeName ?? '');
    setCost(s.costNote ?? '');
    setChain(s.chainLimit);
    setCapacity(s.capacityMax ? String(s.capacityMax) : '');
    setQuorum(s.quorumMin ? String(s.quorumMin) : '');
    if (s.rsvpDeadline) {
      const dd = new Date(s.rsvpDeadline);
      const pad2 = (n: number) => String(n).padStart(2, '0');
      setDeadlineDate(`${dd.getFullYear()}-${pad2(dd.getMonth() + 1)}-${pad2(dd.getDate())}`);
      setDeadlineTime(`${pad2(dd.getHours())}:${pad2(dd.getMinutes())}`);
    }
    if (s.startsAt) {
      const d = new Date(s.startsAt);
      const pad = (n: number) => String(n).padStart(2, '0');
      setWhenMode('exact');
      setDate(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`);
      setTime(s.dateOnly ? '' : `${pad(d.getHours())}:${pad(d.getMinutes())}`);
    } else if (s.timeMode === 'coordinate') {
      setWhenMode('coordinate');
    } else {
      const parsed = s.approxLabel ? parseApprox(s.approxLabel) : null;
      if (parsed?.kind === 'raw') { setWhenMode('free'); setFreeWhen(parsed.text); }
      else if (parsed) { setWhenMode('approx'); setPreset(s.approxLabel!); }
    }
  }

  /**
   * دوستیِ تاییدنشده هم انتخاب‌شدنیه (WHOSUP-204) — با سقفِ سه‌تایی که
   * دیتابیس نگه می‌داره (مایگریشن 0141). تاییدشده‌ها اول.
   */
  const invitable = useMemo(() => {
    const already = new Set((recipients.data ?? []).map((r) => r.recipientId).filter(Boolean));
    return (friends.data ?? [])
      .filter((f) => !already.has(f.profile.id))
      .sort((a, b) => (
        Number(b.status === 'accepted') - Number(a.status === 'accepted')
        || a.profile.displayName.localeCompare(b.profile.displayName)
      ));
  }, [friends.data, recipients.data]);

  /**
   * منبعِ دومِ همون انتخابگر: دوستانی که حساب ندارن (WHOSUP-231).
   *
   * `sms: false` چون مرورگر پیامک نمی‌فرسته — دوستِ فقط-شماره `channel: null`
   * می‌گیره و خاکستری دیده می‌شه. بقیه‌ی قاعده‌ها (یک آدم یک ردیف، «قبلاً
   * دعوت شده») توی core نشسته و تست داره.
   */
  const bookInvitees = useMemo(() => contactInvitees({
    contacts: contactFriends.data ?? [],
    taken: invitedSoFar(recipients.data ?? []).taken,
    callingCode: callingCode.data,
    sms: false,
  }), [contactFriends.data, recipients.data, callingCode.data]);

  const pendingIds = useMemo(
    () => invitable.filter((f) => f.status !== 'accepted').map((f) => f.profile.id),
    [invitable],
  );
  const allowance = useQuery({
    queryKey: ['inviteAllowance', [...pendingIds].sort().join(',')],
    queryFn: () => signalRepo.inviteAllowance(pendingIds),
    enabled: pendingIds.length > 0,
  });

  const CHAIN = [
    { label: t('chainOff'), value: 0 },
    { label: t('chainPlus1'), value: 1 },
    { label: t('chainPlus2'), value: 2 },
    { label: t('chainUnlimited'), value: -1 },
  ];

  /** همون قاعده‌ی فرمِ ساخت: بی‌ساعت، ظهرِ محلی — نه نیمه‌شب. */
  const exactStart = (): Date | null => {
    if (!date) return null;
    const [y, m, d] = date.split('-').map(Number);
    if (!y || !m || !d) return null;
    if (!time) return new Date(y, m - 1, d, 12, 0, 0, 0);
    const [hh, mm] = time.split(':').map(Number);
    return new Date(y, m - 1, d, hh || 0, mm || 0, 0, 0);
  };

  /** همان قاعده‌ی فرمِ ساخت: بی‌ساعت یعنی ظهرِ محلی. */
  const composeDeadline = (): Date | null => {
    if (!deadlineDate) return null;
    const [y, m, d] = deadlineDate.split('-').map(Number);
    if (!y || !m || !d) return null;
    if (!deadlineTime) return new Date(y, m - 1, d, 12, 0, 0, 0);
    const [hh, mm] = deadlineTime.split(':').map(Number);
    return new Date(y, m - 1, d, hh || 0, mm || 0, 0, 0);
  };

  const save = useMutation({
    mutationFn: async () => {
      const start = whenMode === 'exact' ? exactStart() : null;
      const draft: SignalEditDraft = {
        kind,
        title: title.trim(),
        note: note.trim() || null,
        tagIds,
        timeMode: (start ? 'exact'
          : whenMode === 'coordinate' ? 'coordinate' : 'approx') as TimeMode,
        startsAt: start,
        endsAt: null,
        dateOnly: start ? !time : false,
        approxLabel: whenMode === 'approx' ? preset
          : whenMode === 'free' ? freeWhen.trim() || null : null,
        placeName: place.trim() || null,
        costNote: cost.trim() || null,
        capacityMax: kind === 'hosting' && Number(capacity) > 0 ? Number(capacity) : null,
        quorumMin: kind !== 'definite' && Number(quorum) > 0 ? Number(quorum) : null,
        rsvpDeadline: composeDeadline(),
        chainLimit: chain,
      };
      await signalRepo.update(id!, draft);
      // دعوتِ تازه‌ها بعد از ذخیره‌ی محتوا: اعلانِ اون‌ها باید به نسخه‌ی
      // تازه اشاره کنه، نه به چیزی که یه لحظه بعد عوض می‌شه.
      await signalRepo.addRecipients(id!, [...adding], profile!.id);

      // ایمیل و شماره **بعد از** ذخیره، و هر کدوم جدا: محتوا قبلاً ذخیره شده
      // و بقیه خبردار شدن، پس شکستِ یکی از این دو نباید کلِ کار رو
      // شکست‌خورده نشون بده.
      const emails = emailInvitesOf(outside);
      if (emails.length) {
        try { await inviteRepo.byEmail(id!, emails); } catch (e) {
          reportError('web.signal.inviteByEmail', e);
        }
      }
      const phones = phoneInvitesOf(outside);
      if (phones.length) {
        // هیچ پیامکی این‌جا نمی‌ره — فقط ردیف و لینکِ شخصی ساخته می‌شه، تا
        // صفحه‌ی برنامه «کپیِ لینکِ شخصی» داشته باشه.
        try { await inviteRepo.bySms(id!, phones); } catch (e) {
          reportError('web.signal.inviteBySms', e);
        }
      }
      // قیف (WHOSUP-291): آدم‌هایی که در ویرایش اضافه شدند.
      const added = adding.size + emails.length + phones.length;
      if (added) trackFunnel('invite_sent', { id: id!, target: 'friend', count: added });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['signal', id] });
      qc.invalidateQueries({ queryKey: ['recipients', id] });
      qc.invalidateQueries({ queryKey: ['invites', id] });
      qc.invalidateQueries({ queryKey: ['signalVersions', id] });
      qc.invalidateQueries({ queryKey: ['mine'] });
      qc.invalidateQueries({ queryKey: ['inviteAllowance'] });
      nav(`/signal/${id}`, { replace: true });
    },
    onError: (e) => {
      if (handleFilterBlocked(e)) return;
      reportError('web.signal.update', e);
      // سقفِ دعوتِ دوستیِ تاییدنشده (مایگریشن 0141) — دلیلش، نه «خطا در ارسال».
      setError(isInviteCapError(e)
        ? t('inviteCapError', { n: UNACCEPTED_INVITE_CAP })
        : t('errorSending'));
      qc.invalidateQueries({ queryKey: ['inviteAllowance'] });
    },
  });

  const drop = useMutation({
    mutationFn: (rowId: number) => signalRepo.removeRecipient(rowId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recipients', id] }),
    onError: (e) => reportError('web.signal.removeRecipient', e),
  });

  if (signal.isLoading) return <div className="feed"><p className="dim">…</p></div>;
  // سیگنالِ کسِ دیگه رو نمی‌شه ویرایش کرد؛ RLS هم همینو می‌گه. این‌جا فقط
  // برای اینه که کاربر یه فرمِ بی‌فایده نبینه.
  if (!s || s.creatorId !== profile?.id) {
    return <div className="feed"><Card><p className="dim">{t('error')}</p></Card></div>;
  }

  const previewWhen = date
    ? signalWhenLabel({ startsAt: exactStart()?.toISOString(), dateOnly: !time })
    : '';

  return (
    <div className="feed">
      <h1 className="feed-title">{t('editSignal')}</h1>

      <Card>
        <h3>{t('plan')}</h3>
        <input className="input" placeholder={t('titlePlaceholder')} value={title}
          onChange={(e) => setTitle(e.target.value)} />
      </Card>

      <Card>
        <h3>{t('kind')}</h3>
        {/* مثل فرمِ ساخت کارت‌های بازشو، ولی جمع‌شده شروع می‌کنه: انتخاب از
            قبل شده و فرمِ ویرایش نباید با چهار کارتِ باز شروع شه.
            همون قاعده‌ی فرمِ ساخت: سیگنالِ زاده‌ی رخداد «خودم میزبانم»
            نداره، مگر همین حالا انتخاب‌شده باشه (سیگنال‌های قدیمی‌تر). */}
        <PickList value={kind} startOpen={false}
          items={kindsFor({ fromEvent: !!signal.data?.event, current: kind }).map((k) => ({
            value: k.kind, label: `${k.emoji} ${t(k.titleKey)}`, hint: t(k.subKey),
          }))}
          onChange={setKind} />
      </Card>

      {(tags.data ?? []).length ? (
        <Card>
          <h3>{t('category')}</h3>
          <TagPicker value={tagIds} onChange={setTagIds} tags={tags.data ?? []} />
        </Card>
      ) : null}

      <Card>
        <h3>{t('when')}</h3>
        <Select value={whenMode}
          items={[
            { value: 'approx', label: t(APPROX_PRESETS[0].key) },
            { value: 'coordinate', label: t('coordinate') },
            { value: 'exact', label: t('pickDate') },
            { value: 'free', label: t('whenFreeText') },
          ]}
          onChange={(m) => { setWhenMode(m); if (m === 'approx') setPreset(APPROX_PRESETS[0].token); }} />

        {whenMode === 'free' ? (
          <input className="input" placeholder={t('whenFreeTextPlaceholder')} value={freeWhen}
            onChange={(e) => setFreeWhen(e.target.value)} />
        ) : null}

        {whenMode === 'exact' ? (
          <>
            <div className="two-up">
              <label className="field">
                <span>{t('pickDate')}</span>
                <input className="input" type="date" value={date}
                  onChange={(e) => setDate(e.target.value)} />
              </label>
              <label className="field">
                <span>{t('whenFreeText')}</span>
                <input className="input" type="time" value={time}
                  onChange={(e) => setTime(e.target.value)} />
              </label>
            </div>
            {date ? (
              <p className="dim">
                {previewWhen}
                {' · '}
                <button type="button" className="linklike"
                  onClick={() => { setDate(''); setTime(''); }}>{t('clear')}</button>
              </p>
            ) : <p className="dim">{t('pickDayHint')}</p>}
          </>
        ) : null}

      </Card>

      {/* «کجا» کادرِ خودش. */}
      <Card>
        <h3>{t('where')}</h3>
        <input className="input" placeholder={t('placePlaceholder')} value={place}
          onChange={(e) => setPlace(e.target.value)} />
      </Card>

      {/* یک کادر برای همه‌ی «اطلاعاتِ بیشتر» — قرینه‌ی فرمِ ساخت. هزینه هم
          یکی از همون پیشنهادهای پشتِ «؟» است، نه فیلدِ جدا. */}
      <Card>
        <h3>{t('suggestDescription')}<Help lines={SIGNAL_NOTE_KEYS.map((k) => t(k))} /></h3>
        <textarea className="input textarea" rows={4} value={note}
          placeholder={t('suggestDescriptionPh')}
          onChange={(e) => setNote(e.target.value)} />
      </Card>

      <Card>
        {kind === 'hosting' ? (
          <label className="field">
            <span>{t('capacityLabel')}</span>
            <input className="input" type="number" min={1} value={capacity}
              onChange={(e) => setCapacity(e.target.value)} />
          </label>
        ) : null}
        {kind !== 'definite' ? (
          <label className="field">
            <span>{t('quorumLabel')}</span>
            <input className="input" type="number" min={2} value={quorum}
              onChange={(e) => setQuorum(e.target.value)} />
          </label>
        ) : null}
      </Card>

      <Card>
        {/* توضیح پشتِ «؟» — همیشه‌دیده‌شدن جای فرم را می‌گرفت (WHOSUP-282). */}
        <h3>{t('whoToTell')}<Help text={t('cantRemoveWhoIsIn')} /></h3>
        {(recipients.data ?? []).map((r) => (
          <div key={r.id} className="tally-row">
            <span>
              {r.profile?.displayName ?? r.invitedName
                ?? r.invitedEmail ?? r.invitedPhone ?? t('guest')}
            </span>
            {r.response === 'in'
              ? <span className="count ok">{t('respIn')}</span>
              : (
                <button type="button" className="linklike" disabled={drop.isPending}
                  onClick={() => drop.mutate(r.id)}>{t('remove')}</button>
              )}
          </div>
        ))}

        {invitable.length || bookInvitees.length ? (
          <>
            <h4>{t('addMorePeople')}</h4>
            {/* همون انتخابگرِ فرمِ ساخت و همون دو منبع (WHOSUP-231)، تا «اضافه
                کردنِ آدم» توی دو فرم دو جور به نظر نرسه. کلیدها پیشوند دارن
                چون یه فهرستِ واحده و شناسه‌ی مخاطب عدد است و شناسه‌ی آدم UUID. */}
            <MultiSelect logKey="invite" placeholder={t('pickPeople')}
              value={[
                ...[...adding].map((uid) => `u-${uid}`),
                ...outside.filter((c) => c.contactId != null).map((c) => `c-${c.contactId}`),
              ]}
              items={[
                ...invitable.map((f) => {
                  const rem = f.status === 'accepted' ? -1 : allowance.data?.get(f.profile.id);
                  const cap = inviteCapHint(rem);
                  return {
                    key: `u-${f.profile.id}`,
                    label: f.profile.displayName,
                    hint: cap ? t(cap.key, { n: cap.n }) : undefined,
                    searchText: `${f.profile.displayName} ${f.profile.username}`,
                    disabled: !canInvite(rem),
                  };
                }),
                // تهِ فهرست، نه لابه‌لای بقیه: بی‌حساب‌ها با ایمیل خبردار
                // می‌شن و فقط-شماره‌ای‌ها اصلاً انتخاب‌شدنی نیستن.
                ...bookInvitees.map((r) => ({
                  key: `c-${r.contact.id}`,
                  label: r.contact.name,
                  hint: r.channel === 'email' ? t('willGetEmail')
                    : r.contact.phones.length ? t('phoneOnlyCantPick') : t('noWayToReach'),
                  searchText: `${r.contact.name} ${r.contact.emails.join(' ')} ${r.contact.phones.join(' ')}`,
                  disabled: !r.channel,
                })),
              ]}
              onToggle={(key) => {
                if (key.startsWith('c-')) {
                  const row = bookInvitees.find((r) => `c-${r.contact.id}` === key);
                  if (row) toggleBookContact(row);
                  return;
                }
                const uid = key.slice(2);
                setAdding((prev) => {
                  const next = new Set(prev);
                  if (next.has(uid)) next.delete(uid); else next.add(uid);
                  return next;
                });
              }} />
          </>
        ) : null}

        {/* ــ دعوتِ آدمِ تازه از بیرونِ اپ (WHOSUP-203) ــ
            جمع‌شده شروع می‌کنه، چون کارِ اکثرِ ویرایش‌ها نیست: کسی که یادش
            رفته یه نفر رو خبر کنه، خودش بازش می‌کنه. */}
        {/* فقط آدرس‌های تایپ‌شده: مخاطبِ انتخاب‌شده از فهرست، چیپش رو از خودِ
            انتخابگر گرفته و تکرارش این‌جا یعنی یه آدم دو بار روی صفحه. */}
        {outside.some((c) => c.contactId == null) ? (
          <div className="chiprow chips-wrap">
            {outside.filter((c) => c.contactId == null).map((c) => (
              <button key={`${c.channel}:${c.value}`} type="button" className="chip on"
                title={c.value}
                onClick={() => setOutside(removeDraftContact(outside, c.channel, c.value))}>
                {draftContactLabel(c)} ×
              </button>
            ))}
          </div>
        ) : null}

        <button type="button" className="linklike" aria-expanded={outsideOpen}
          onClick={() => setOutsideOpen((x) => !x)}>
          {outsideOpen ? `${t('inviteOutside')} ▴` : `+ ${t('inviteOutside')}`}
        </button>

        {outsideOpen ? (
          <>
            <p className="dim">{t('inviteReachSub')}</p>
            <div className="two-up">
              <input className="input" inputMode="email"
                placeholder={t('inviteReachPlaceholder')}
                value={reachDraft}
                onChange={(e) => { setReachDraft(e.target.value); setReachError(null); }}
                // Enter این‌جا یعنی «اضافه کن»، نه «ویرایش رو ذخیره کن».
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addOutside(); } }} />
              <input className="input"
                placeholder={t('inviteNamePlaceholder')}
                value={reachName}
                onChange={(e) => setReachName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addOutside(); } }} />
            </div>
            {reachError ? <p className="error">{reachError}</p> : null}
            <button type="button" className="btn" disabled={!reachDraft.trim()}
              onClick={addOutside}>
              {t('inviteEmailAdd')}
            </button>
            {/* شماره روی وب پیامک نمی‌شه — لینکش از صفحه‌ی برنامه کپی می‌شه. */}
            <p className="dim">{t('copyInviteLinkHint')}</p>
          </>
        ) : null}
      </Card>

      {/* زنجیره و مهلت پشتِ «گزینه‌های بیشتر»، تهِ فرم — قرینه‌ی فرمِ ساخت
          (WHOSUP-282). مقدارِ غیرپیش‌فرض بخش را باز نگه می‌دارد تا پنهان نماند. */}
      <Card>
        <button type="button" className="linklike" aria-expanded={moreShown}
          onClick={() => setMoreOpen((x) => !x)}>
          {moreShown ? `${t('moreSignalOptions')} ▴` : `+ ${t('moreSignalOptions')}`}
        </button>
        {moreShown ? (
          <>
            {/* توضیحِ زنجیره پشتِ «؟» — قرینه‌ی فرمِ ساخت. */}
            <h3>{t('chainInvite')}<Help text={t('chainNote')} /></h3>
            {/* مقدارِ Select رشته‌ست؛ عدد بودنِ chain همین‌جا ترجمه می‌شه. */}
            <Select value={String(chain)}
              items={CHAIN.map((c) => ({ value: String(c.value), label: c.label }))}
              onChange={(v) => setChain(Number(v))} />
            <label className="field">
              <span>{t('deadlineLabel')}</span>
              <div className="two-up">
                <input className="input" type="date" value={deadlineDate}
                  onChange={(e) => setDeadlineDate(e.target.value)} />
                <input className="input" type="time" value={deadlineTime}
                  onChange={(e) => setDeadlineTime(e.target.value)} />
              </div>
            </label>
          </>
        ) : null}
      </Card>

      {error ? <p className="error">{error}</p> : null}

      <button type="button" className="btn fill block"
        disabled={save.isPending || !title.trim()}
        onClick={() => { setError(null); save.mutate(); }}>
        {save.isPending ? '…' : t('editSignalSave')}
      </button>
    </div>
  );
}
